// imports
import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Route, Routes,
  Navigate, BrowserRouter as Router
} from 'react-router-dom';

import { ROUTES } from '../constants';
import Protected from '../layouts/protected';
import { DOM_UTILS, DATE_UTILS } from '../common/utils';
import { clearState } from '../repositories/store/actions';
import { useConnection } from '../repositories/web3/session';
import Home from './home';
import Game from './game';
import Heroes from './heroes';
import Profile from './profile';
import Backpack from './backpack';
import Quests from './quest/list';
import QuestCrew from './quest/crew';
import Activity from './profile/activity';
import Settings from './profile/settings';
import HeroCollection from './profile/heroCollection';
import ItemCollection from './profile/itemCollection';

// exports
export default function App(): JSX.Element {
  const homePath = ROUTES.HOME;
  const heroListPath = ROUTES.HEROES;
  const questListPath = ROUTES.QUESTS;
  const backpackPath = ROUTES.BACKPACK;
  const activityPath = ROUTES.ACTIVITY;
  const settingsPath = ROUTES.SETTINGS;
  const questCrewPath = ROUTES.QUEST_CREW;
  const heroCollectionPath = ROUTES.HERO_COLLECTION;
  const itemCollectionPath = ROUTES.ITEM_COLLECTION;
  // hooks configuration
  const dispatch = useDispatch();
  const cookieExpiryTimeout = useRef<NodeJS.Timeout>();
  const { isConnected } = useConnection({ onDisconnectedCB: () => dispatch(clearState()) });
  useEffect(() => {
    const handleWheel = (event: WheelEvent): void => { if (event.ctrlKey || event.metaKey) { event.preventDefault(); } };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if ((event.ctrlKey || event.metaKey) && (event.key === '=' || event.key === '-' || event.key === '+')) { event.preventDefault(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });

    const cookieTimeout = DOM_UTILS.getCookie('WL-Access-Expires') as number;
    const remainingTime = DATE_UTILS.getRemainingMS(cookieTimeout || 0);
    if (!cookieExpiryTimeout.current && remainingTime > 0) {
      cookieExpiryTimeout.current = setTimeout(() => { window.location.href = '/'; }, remainingTime);
    }

    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  // render
  return (
    <Router>
      <Helmet>
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image" content="/opengraph.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image:type" content="image/png" />
        <meta name="twitter:image:width" content="1600" />
        <meta name="twitter:image:height" content="900" />
        <meta name="twitter:image" content="/twitter.png" />
      </Helmet>
      <Routes>
        {/* UnProtected Routes */}
        <Route path={homePath} element={<Home />} />
        <Route path={ROUTES.PROFILE} element={<Navigate to={heroCollectionPath} />} />
        {/* Protected Routes - Game */}
        <Route
          path={ROUTES.GAME}
          element={(<Protected isAuthenticated={isConnected} origin={ROUTES.GAME} outlet={<Game />} />)}
        />
        {/* Protected Routes - Profile */}
        <Route
          path={activityPath}
          element={(<Protected isAuthenticated={isConnected} origin={activityPath} outlet={<Profile><Activity /></Profile>} />)}
        />
        <Route
          path={settingsPath}
          element={(<Protected isAuthenticated={isConnected} origin={settingsPath} outlet={<Profile><Settings /></Profile>} />)}
        />
        <Route
          path={heroCollectionPath}
          element={(<Protected isAuthenticated={isConnected} origin={heroCollectionPath} outlet={<Profile><HeroCollection /></Profile>} />)}
        />
        <Route
          path={itemCollectionPath}
          element={(<Protected isAuthenticated={isConnected} origin={itemCollectionPath} outlet={<Profile><ItemCollection /></Profile>} />)}
        />
        {/* Protected Routes - Heroes */}
        <Route
          path={heroListPath}
          element={(<Protected isAuthenticated={isConnected} origin={heroListPath} outlet={<Heroes />} />)}
        />
        {/* Protected Routes - Backpack */}
        <Route
          path={backpackPath}
          element={(<Protected isAuthenticated={isConnected} origin={backpackPath} outlet={<Backpack />} />)}
        />
        {/* Protected Routes - Quests */}
        <Route
          path={questListPath}
          element={(<Protected isAuthenticated={isConnected} origin={questListPath} outlet={<Quests />} />)}
        />
        <Route
          path={questCrewPath}
          element={(<Protected isAuthenticated={isConnected} origin={questCrewPath} outlet={<QuestCrew />} />)}
        />
        {/* Fallback Routes */}
        <Route path="*" element={<Navigate to={homePath} />} />
      </Routes>
    </Router>
  );
}
